/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { toCheckLocations } from "../../../../actions/Checks/checkSelection";
import { Check, CHECK_CARD_TYPE, Layer } from "../../../../actions/Layers/constants";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { path_send_check_status_to_tenant, path_summary } from "../../../../actions/Tenants/config/constants";
import { useLayerChecks } from "../../../../hooks/useLayerChecks";
import { getFilledArrayOrDefault } from "../../../../utils";
import { dayjs } from "../../../../utils/date";
import { FromLink } from "../../../../utils/useNavigateFrom";
import { Loading } from "../../../Helper/Loading";

import { useRouteLayer, useRouteLayerQuery } from "../../../../hooks/useCurrentEntities";

type CheckCardProps = {
    check: Check;
    layer: Layer;
    card_type: any;
    lastStep: string;
    captionField?: string;
}

const CheckCard = ({ check, layer, card_type, lastStep, captionField }: CheckCardProps) => {
    // Check meta is flattened onto the check, so the caption can be a check field or a meta key
    const caption = captionField ? check[captionField] : undefined;

    const getFlagModifier = (check) => {
        // * get status from internal data processing
        if (check.status) {
            return `flag-${check.status}`;
        }

        return "";
    };

    const getInnerCard = (check) => {
        if (card_type === CHECK_CARD_TYPE.LABEL) {
            return (
                <div className="display-6 fw-bold" css={css`font-size: 18px;`}>{layer.label}</div>
            );
        }

        if (card_type === CHECK_CARD_TYPE.SIZE_CATEGORY_OR_LABEL) {
            if (check.size_category) {
                return (
                    <div className="display-6 fw-bold" css={css`font-size: 18px;`}>{check.size_category}</div>
                );
            }
            return (
                <div className="display-6 fw-bold" css={css`font-size: 18px;`}>{layer.label}</div>
            );
        }

        if (card_type === CHECK_CARD_TYPE.SIZE_CATEGORY) {
            return (
                <div className="display-6 fw-bold" css={css`font-size: 18px;`}>{check.size_category}</div>
            );
        }

        if (card_type === CHECK_CARD_TYPE.DATETIME) {
            const localDateTime = dayjs.utc(check.created).local();
            return (
                <div>
                    <div className="display-6 fw-bold" css={css`font-size: 18px;`}>{localDateTime.format("D MMM")}</div>
                    <div className="text-muted" css={css`font-size: 12px;`}>{localDateTime.format("HH:mm")}</div>
                </div>
            );
        }

        // Default: DATE card type
        const localDate = dayjs.utc(check.created).local();
        return (
            <div>
                <div className="display-6 fw-bold" css={css`font-size: 18px;`}>{localDate.format("D")}</div>
                <div className="text-muted fw-bold">{localDate.format("MMM")}</div>
            </div>
        );
    };

    return (
        <FromLink to={`/layer/${check.layer_id}/add-check/${check.check_id}/${lastStep}`} css={css`color: unset;`}>
            <div
                className={`card-alert --${getFlagModifier(check)} text-center py-2 px-3 clickable me-0 mb-0`}
                css={css`min-width: 4.3rem; border-top-width: 6px; border-radius: 8px; border-left-width: 0px; border-right-width: 0px; border-bottom-width: 0px;`}
                title={`Check ${check.check_id}`}
            >
                {getInnerCard(check)}
                {caption !== undefined && caption !== null && caption !== "" && (
                    <div className="text-muted text-truncate" css={css`font-size: 12px; max-width: 10rem;`} title={String(caption)}>{String(caption)}</div>
                )}
            </div>
        </FromLink>
    );
};

function filterOnlyLatestCheckPerChildPerLocation(checks: any, card_type: any) {
    // DATE is default card type, in that sense we want to show all checks
    if (!card_type || card_type === CHECK_CARD_TYPE.DATE) {
        return checks;
    }

    const getCompositeKey = (check) => {
        // do not be confused by label, it is label of the card, not layer label
        const { layer_id, label, location, size_category } = check;
        switch (card_type) {
        case CHECK_CARD_TYPE.SIZE_CATEGORY_OR_LABEL:
            return `${layer_id}_${size_category || label}_${location}`;
        case CHECK_CARD_TYPE.SIZE_CATEGORY:
            return `${layer_id}_${size_category}_${location}`;
        case CHECK_CARD_TYPE.LABEL:
            return `${layer_id}_${label}_${location}`;
        default:
            return `${layer_id}_${location}`;
        }
    };
    // Create a Map to store the latest checks for each location and child
    const latestChecksMap = new Map();

    // Iterate through the data and update the latest checks based on configuration
    checks.forEach((check) => {
        const { location_config } = check;
        if (location_config?.only_show_latest_check_on_child) {
            const compositeKey = getCompositeKey(check);
            if (!latestChecksMap.has(compositeKey) || latestChecksMap.get(compositeKey).check_id < check.check_id) {
                latestChecksMap.set(compositeKey, check);
            }
        }
    });

    // Use filter to exclude previous checks from the original array
    return checks.filter((check) => {
        const { location_config } = check;
        if (!location_config) return true;

        // This only applies for specific locations
        if (!location_config?.only_show_latest_check_on_child) {
            return true;
        }
        const compositeKey = getCompositeKey(check);
        return latestChecksMap.has(compositeKey) && latestChecksMap.get(compositeKey).check_id === check.check_id;
    });
}

export default function LayerChecks({ subconfig }) {
    const config = useConfig();
    const layer = useRouteLayer();
    const isLoading = useRouteLayerQuery().isLoading;
    const { data: checks = [], isLoading: checksLoading } = useLayerChecks(layer?.tenant_id, layer?.id ?? undefined, {
        view_strategy: subconfig?.view,
        with_meta: true,
        children_level: 2,
    });

    if (isLoading || !checks || checksLoading) {
        return <Loading />;
    }

    const { filter_field, filter_location } = subconfig;
    const filterLocations = toCheckLocations(filter_location);

    const addData = (check) => {
        const child = getChild(check);

        // Make sure there is a fruit_type set on the layer (inherit from parent)
        const layer_for_get_check_type_config = child ? { ...child, fruit_type: child.fruit_type || layer.fruit_type } : layer;
        const location_config = config.get_check_type_config(layer_for_get_check_type_config, check);

        const flow = getFilledArrayOrDefault(location_config?.flow?.map((f) => f.path), [path_summary]);
        // Post-summary steps (like send-external-notification) are not a landing page
        // for existing checks — always land on summary or the last pre-summary step.
        const postSummarySteps = [path_send_check_status_to_tenant];
        const lastStep = [...flow].reverse().find((step) => !postSummarySteps.includes(step)) || path_summary;

        const card = (
            <CheckCard
                layer={child || layer}
                check={check}
                card_type={subconfig.card_type}
                lastStep={lastStep}
                captionField={location_config?.card_caption_field}
            />
        );

        return {
            ...check,
            location_config,
            layer: child || layer,
            card,
            is_manual: check.is_manual === true // lets be more strict about what is true or false here (null should be false to)
        };
    };

    const getChild = (test) => getFilledArrayOrDefault(layer.children).find((i) => i.id === test.layer_id);

    // label every location that is unconfigured as other
    let theChecks = checks
        .filter((i) => (filter_field && layer.id !== i.layer_id ? i[filter_field] === layer[filter_field] : true))
        .filter((i) => (filterLocations.length && layer.id !== i.layer_id ? filterLocations.includes(i.location) : true))
        .map(addData)
        // .filter((i) => i.layer.type !== LAYER_TYPE.PURCHASE_ORDER)
        // ! commented out by florian, blame me, but I think this should not be needed
        .sort((a, b) => a.check_id - b.check_id);

    // If we do not show a date on the card... we want to only show the latest label e.g. advance ripening
    theChecks = filterOnlyLatestCheckPerChildPerLocation(theChecks, subconfig.card_type);

    let checkLocations = Array.from(new Set(theChecks.map((check) => check.location)));
    checkLocations = checkLocations.map((location) => ({
        location,
        config: config.get_raw_check_location(location as string),
        checks: theChecks.filter((check) => check.location === location),
    }));

    return (
        <div className="w-100 d-flex flex-column mx-3n" css={css`gap: 2rem;`}>
            {checkLocations
                .filter((checkType: any) => (checkType.checks.length > 0))
                .map((checkType: any) => (
                    <div key={checkType.location} className="pb-3 bg-white">
                        <div><h3>{checkType.config.text}</h3></div>
                        <div className="d-flex" css={css`gap: 1rem; flex-wrap: wrap;`}>
                            {checkType.checks.map((check, index) => (
                                <div css={css`width: fit-content;`} key={index}>
                                    {check.card}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
        </div>
    );
}
